import {
  take,
  call,
  put,
  select,
  fork,
  takeLatest,
  race,
  all,
} from 'redux-saga/effects';
import _ from 'lodash';
import {
  GO_TO,
  GO_TO_DETAILS_VIEW,
  FETCH_EHUB_AUTH_TOKEN,
  OPEN_EHUB_DISCLAIMER,
  MYPRIVILEGES_MODAL_DISPLAY,
  FETCH_MY_PRIVILEGES_FEATURES,
  FETCH_MY_PRIVILEGES_FEATURES_SUCCESS,
  FETCH_MY_PRIVILEGES_FEATURES_FAILURE,
  CTA_BUTTON_ONCLICK,
  setCtnButtonLoading,
  setCtnButtonNotLoading,
  setPageLoading,
  setPageNotLoading,
  FETCH_DETAILS_PAGE_INFO,
  UPDATE_DETAILS_PAGE_INFO,
} from '../actions/myPrivilegesAction';
import { goto } from '../../../actions/historyActions';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import {
  getAuthToken,
  getCardViewState,
  getAuthTokenParamName,
  getMyPrivilegesFeatureUrl,
} from '../selectors/myPrivilegesSelector';
import { getMyPrivileges } from '../../ps-general/selectors/myPrivilegesSelector';
import uuidv4 from 'uuid/v4';
import { getLanguage } from '../../../selectors/userSelectors';
import { openDisclaimer } from '../../../modules/emma-common/actions';
import { REDIRECT_ACTION } from '../../../modules/partners/actions';
import { PostMsgParams } from '../../../modules/partners/types/partnerTypes';
import { MyPrivilege } from '../../ps-general/reducers/myPrivilegesReducer';
import browserHistory from '../../../stores/browserHistory';
import { getToken } from '../../../selectors/authSelectors';
import { getValidToken, isApp } from '../../../utils/index';
import { handleGaError } from '../../../utils/generalGaEventAnalytics';
import { FRAME_APP_MOUNTED } from '../../../actions/frameActions';
import { getMyPrivilegesFeatures } from '../../home/selectors/serviceSelectors';
import env from '../../../env';

function* fetchMyPrivilegesAuthToken(action: any) {
  const {
    url,
    fetchMyPrivilegesAuthTokenSuccess,
    fetchMyPrivilegesAuthTokenFailure,
  } = action.payload;
  try {
    if (!url) {
      return;
    }

    yield put(setCtnButtonLoading());

    const language = yield select(getLanguage);
    const languageUpperCase = language ? language.toUpperCase() : '';
    const urlWithParam = url.replace('${language}', languageUpperCase);
    const res = yield call(myAxaFetchWithToken, urlWithParam, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-axahk-msgid': uuidv4(),
      },
    });

    if (res && res.status === 200) {
      const data = yield res.json();
      const { retrieveEHubResponse } = data;
      if (retrieveEHubResponse) {
        const { authToken } = retrieveEHubResponse;
        yield put(fetchMyPrivilegesAuthTokenSuccess(authToken));
        yield put(setCtnButtonNotLoading());
      } else {
        throw res;
      }
    } else {
      throw res;
    }
  } catch (e) {
    yield put(fetchMyPrivilegesAuthTokenFailure(e));
  }
}

function* goToRedirect(action: any) {
  if (action && action.payload) {
    const { url, cardViewId } = action.payload;
    const authToken = yield select(getAuthToken, cardViewId);
    const authTokenParamName = yield select(getAuthTokenParamName, cardViewId);

    const postMsgParams: PostMsgParams = {};
    postMsgParams.action = REDIRECT_ACTION;
    if (authToken && authTokenParamName) {
      postMsgParams.params = {};
      postMsgParams.params[authTokenParamName] = authToken;
    }
    yield call(goto, url, undefined, postMsgParams);
  }
}

function* goToDetailsView(action: any) {
  if (action && action.payload) {
    const { url, myprivileges_details_id, moduleName } = action.payload;
    const myPrivileges: MyPrivilege[] = yield select(getMyPrivileges);
    const foundPrivilege = myPrivileges.find(
      (p) => p.moduleName === moduleName,
    );

    const id = myprivileges_details_id
      ? myprivileges_details_id
      : foundPrivilege && foundPrivilege.featureId;
    const webDeepLink = url.replace(/\$\{myprivileges_details_id\}/, id);

    const tcWindow: any = window;
    const token = yield select(getToken);
    const aToken = getValidToken(token);
    if (aToken) {
      try {
        tcWindow.dataLayer.push({
          event: 'screenView',
          page: `/emma-webportal/${webDeepLink}`,
          userId: aToken,
        });
        // HKEMMA-100917 - GA3 Decommission - Remove GA3
        // if (!isApp()) {
        //   tcWindow.tC.event.virtualPageview(tcWindow, {
        //     page: `/emma-webportal/${webDeepLink}`,
        //     AXAID: aToken,
        //   });
        // }
      } catch (e) {
        handleGaError(e);
      }
    }

    browserHistory.push(`/${webDeepLink}`);
  }
}

function* openMyPrivilegesDisclaimer(action: any) {
  if (action && action.payload) {
    const { disclaimerId, cardViewId } = action.payload;
    const authToken = yield select(getAuthToken, cardViewId);
    const authTokenParamName = yield select(getAuthTokenParamName, cardViewId);
    const postMsgParams: PostMsgParams = {};
    postMsgParams.action = REDIRECT_ACTION;
    if (authToken && authTokenParamName) {
      postMsgParams.params = {};
      postMsgParams.params[authTokenParamName] = authToken;
    }
    yield put(openDisclaimer(disclaimerId, postMsgParams));
  }
}

function* fetchFeatures(): Iterable<any> {
  const isFetched = yield select(getMyPrivilegesFeatures);
  if (isFetched) return;

  const myPrivilegesFeatureUrl = yield select(getMyPrivilegesFeatureUrl);
  try {
    if (!myPrivilegesFeatureUrl) {
      return;
    }
    // const res = { status: 200, json: () => require('../mock/myprivilegesFeature.json') }
    const url = `${myPrivilegesFeatureUrl}?device=WEB`;
    const res = yield call(myAxaFetchWithToken, url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-axahk-msgid': uuidv4(),
      },
    });

    const data = yield res.json();
    if (!data) throw new Error();
    const features =
      data && data.retrievePriviliegesResponse
        ? data.retrievePriviliegesResponse
        : [];
    yield put({
      type: FETCH_MY_PRIVILEGES_FEATURES_SUCCESS,
      featuresItems: features,
    });
  } catch (e) {
    yield put({ type: FETCH_MY_PRIVILEGES_FEATURES_FAILURE, error: e });
  }
}

function* updateDetailsPageInfo(cardViewId, data) {
  yield put({
    type: UPDATE_DETAILS_PAGE_INFO,
    payload: { cardViewId, data },
  });
}

function* fetchDetailsPageInfo(action) {
  // TODO: rewrite this function
  const aLakeApi = `${env.ALAKE_API_ENDPOINT}/api/emma`;

  yield put(setPageLoading());

  const cardViewId = action.payload.cardViewId;

  const url = (() => {
    switch (cardViewId) {
      case 'cdp':
        return `${aLakeApi}/vendorPurchaseStatus`;
      default:
        return null;
    }
  })();

  const errorCodePrefix = (() => {
    switch (cardViewId) {
      case 'cdp':
        return 'CDP';
      default:
        return null;
    }
  })();

  if (url) {
    try {
      let errorCode = '';
      const errorMessage = '';

      const res = yield call(
        myAxaFetchWithToken,
        url,
        {
          method: 'GET',
        },
        undefined,
        (err, resp) => {
          return resp
          .json()
          .then(data => {
            errorCode = data?.retrievePurchaseStatus?.reasonCode
            return Boolean(errorCode);
          })
          .catch(() => {
            return false;
          });
        },
      );

      if (errorCode) {
        const error = new Error();
        error.message = errorMessage;
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'code' does not exist on type 'Error'.
        error.code = errorCode;
        throw error;
      }

      const data = yield res.json();

      yield updateDetailsPageInfo(cardViewId, data);
    } catch (err) {
      console.error(err);
      yield showErrorMessage(err.code ? err.code : `${errorCodePrefix}-DR02`);
    }
  }

  yield put(setPageNotLoading());
}

function* showErrorMessage(errorCode: string) {
  goto(`emma-general-error-code?error_code=${errorCode}`);
  yield put(setPageNotLoading());
}

function* myPrivilegesSaga() {
  yield takeLatest(FETCH_EHUB_AUTH_TOKEN, fetchMyPrivilegesAuthToken);
  yield takeLatest(GO_TO, goToRedirect);
  yield takeLatest(GO_TO_DETAILS_VIEW, goToDetailsView);
  yield takeLatest(OPEN_EHUB_DISCLAIMER, openMyPrivilegesDisclaimer);
  yield takeLatest(FETCH_MY_PRIVILEGES_FEATURES, fetchFeatures);
  yield takeLatest(FETCH_DETAILS_PAGE_INFO, fetchDetailsPageInfo);
  // yield take(FRAME_APP_MOUNTED);
  yield fork(fetchFeatures);
}

export default myPrivilegesSaga;
